import React, { Fragment } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { DISPLAY_REM } from 'Components/frameworks/image/constants';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { usePageContext } from 'Context/PageContext';
import { t } from 'Src/i18n';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import type { IconName } from 'Components/frameworks/icon/Icon';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Image } from 'Components/frameworks/image/Image';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { Slider } from 'Components/frameworks/slider/Slider';
import { Text } from 'Components/frameworks/text/Text';
import { LineTitle } from 'Components/widgets/line-title/LineTitle';
import { asRatioDisplay } from 'Src/types/cast-helpers';
type BrandPromiseLineItem = {
  iconName: IconName;
  index?: number;
  isExclusiveCollection?: boolean;
  link?: string;
  mediaAlt?: string;
  mediaUrl: string;
  text: string;
};
type Props = {
  id?: string;
  items: Array<BrandPromiseLineItem>;
  learnMoreLink?: string;
  learnMoreLabel?: string;
  title?: string;
};
const IMAGE_SIZE = {
  h: 235,
  w: 180,
};
const RATIO_DISPLAYS = [
  {
    value: 1 / 6,
    unit: DISPLAY_REM,
  },
  {
    value: 180,
    unit: 'px',
  },
  {
    value: 180,
    unit: 'px',
  },
  {
    value: 180,
    unit: 'px',
  },
];

const Item = ({
  showPicture,
  text,
  iconName,
  isExclusiveCollection,
  mediaUrl,
  mediaAlt,
  link,
  index,
}: BrandPromiseLineItem & { showPicture: boolean }) => {
  const TagName = link ? DynamicLink : Fragment;
  const pageContext = usePageContext();
  const siteSection = pageContext?.siteSection || '';
  const track = useTracking({
    actionGA: 'push_content_brandPromise',
    labelGA: `${iconName ?? ''}`.trim(),
    thumbnailList: `${siteSection}_brandPromise`,
    thumbnailPosition: index + 1,
  });
  const conditionalTagNameProps = link
    ? {
        onClick: () => track(),
        url: link,
      }
    : {};
  const badgeProps = {
    containerClassName: 'rounded-full',
    iconClassName: '',
    iconWidth: isExclusiveCollection ? '3.4375rem' : '1.9375rem',
    isNotSquare: false,
  };
  return (
    <div
      key={text}
      className={classnames('group relative isolate')}
      data-testid="BrandPromiseLine-item"
    >
      {showPicture && (
        <div className="relative isolate flex h-[235px] w-180 shrink-0 items-end justify-center overflow-hidden rounded-12 p-10 text-center">
          <Image
            altMsg={mediaAlt}
            className="transition-transform absolute inset-0 transform duration-250 ease-in group-hover:scale-105 group-focus:scale-105"
            dimensions={IMAGE_SIZE}
            imageUrl={mediaUrl}
            ratioDisplay={asRatioDisplay(RATIO_DISPLAYS)}
          />
        </div>
      )}
      <TagName {...conditionalTagNameProps}>
        {showPicture && <div className="relative -mt-[235px] h-[235px]" />}
        <div className="w-180 text-black">
          <div
            className={classnames('relative m-auto h-[72px] w-[126px] overflow-hidden', {
              '-mt-42': showPicture,
            })}
          >
            <div
              className={classnames(
                'm-auto flex size-84 items-center justify-center bg-white',
                badgeProps.containerClassName,
              )}
            >
              <Icon
                className={classnames(badgeProps.iconClassName, 'text-sienna')}
                isNotSquare={badgeProps.isNotSquare}
                name={iconName}
                width={badgeProps.iconWidth}
              />
            </div>
          </div>
          <Text className="text-center text-14 text-current" text={text} />
        </div>
      </TagName>
    </div>
  );
};

export const BrandPromiseLine = ({ id, title, items, learnMoreLink, learnMoreLabel }: Props) => {
  // use isDesktop to display slider's controls only on desktops
  const { isTablet, isDesktop } = useResponsive();
  const track = useTracking({
    actionGA: 'push_content_brandPromise',
    labelGA: 'seeMore',
  });
  const showPicture = items.every((item) => !!item.mediaUrl);
  return (
    <SiteContainer className="Line" id={id}>
      <div className="flex flex-wrap justify-between">
        {!!title && <LineTitle className="flex-1 md:flex-none">{title}</LineTitle>}
        {learnMoreLabel && learnMoreLink && (
          <DynamicLink
            className="order-last m-auto mt-15 text-14 text-black underline hocus:text-black-active md:order-0 md:mx-0 md:mt-5"
            onClick={() => track()}
            url={learnMoreLink}
          >
            {learnMoreLabel}
          </DynamicLink>
        )}
        <div className="-mx-20 w-screen lg:mx-0 lg:w-full">
          <Slider
            cols={0}
            hasControls={isDesktop}
            navPosition="center"
            nbItemsToScroll={isTablet ? 2 : 1}
            nextLabel={t('common.next')}
            previousLabel={t('common.previous')}
            scrollAreaContainerClassName="gap-20 px-20 lg:px-0 scrollbar-hidden scroll-x scroll-smooth"
            scrollControlsClassName="-mx-20 lg:mx-0"
          >
            {items.map((item, index) => (
              <Item {...item} index={index} key={item.text} showPicture={showPicture} />
            ))}
          </Slider>
        </div>
      </div>
    </SiteContainer>
  );
};
